import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { Container, ActionButton } from './ReferenceManagement.styles';

const { Option } = Select;
const { TextArea } = Input;

interface ReferenceManagementProps {
  type: 'regions' | 'provinces' | 'communes' | 'departements' | 'groupeClients' | 'typologies';
  title: string;
  fetchAction: any;
  createAction: any;
  updateAction: any;
  deleteAction: any;
  columns: any[];
  formFields: any[];
}

const ReferenceManagement: React.FC<ReferenceManagementProps> = ({
  type,
  title,
  fetchAction,
  createAction,
  updateAction,
  deleteAction,
  columns,
  formFields,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const { [type]: data, loading } = useSelector((state: RootState) => state.references);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');

  useEffect(() => {
    dispatch(fetchAction());
  }, [dispatch, fetchAction]);

  const handleCreate = () => {
    setModalMode('create');
    setEditingItem(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (item: any) => {
    setModalMode('edit');
    setEditingItem(item);
    form.setFieldsValue(item);
    setIsModalVisible(true);
  };

  const handleView = (item: any) => {
    setModalMode('view');
    setEditingItem(item);
    form.setFieldsValue(item);
    setIsModalVisible(true);
  };

  const handleDelete = (item: any) => {
    Modal.confirm({
      title: `Supprimer ${title.toLowerCase()}`,
      content: `Êtes-vous sûr de vouloir supprimer cet élément ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await dispatch(deleteAction(item.id));
          message.success(`${title} supprimé avec succès`);
        } catch (error) {
          message.error('Erreur lors de la suppression');
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (modalMode === 'create') {
        await dispatch(createAction(values));
        message.success(`${title} créé avec succès`);
      } else if (modalMode === 'edit' && editingItem) {
        await dispatch(updateAction({ id: editingItem.id, data: values }));
        message.success(`${title} modifié avec succès`);
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Erreur lors de la sauvegarde');
    }
  };

  const actionsColumn = {
    title: 'Actions',
    key: 'actions',
    width: 150,
    render: (_: any, item: any) => (
      <Space size="small">
        <Tooltip title="Voir">
          <ActionButton
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(item)}
          />
        </Tooltip>
        <Tooltip title="Modifier">
          <ActionButton
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(item)}
          />
        </Tooltip>
        <Tooltip title="Supprimer">
          <ActionButton
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(item)}
          />
        </Tooltip>
      </Space>
    ),
  };

  const tableColumns = [...columns, actionsColumn];

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>{title}</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Ajouter
        </Button>
      </div>

      <Card>
        <Table
          columns={tableColumns}
          dataSource={data as any[]} // Fix: cast to any[] to avoid type issues
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `Total: ${total} éléments`,
          }}
        />
      </Card>

      <Modal
        title={
          modalMode === 'create' 
            ? `Nouveau ${title.toLowerCase()}` 
            : modalMode === 'edit' 
            ? `Modifier ${title.toLowerCase()}` 
            : `Détails ${title.toLowerCase()}`
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={
          modalMode === 'view' ? (
            <Button onClick={() => setIsModalVisible(false)}>Fermer</Button>
          ) : (
            [
              <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                Annuler
              </Button>,
              <Button key="submit" type="primary" onClick={() => form.submit()} loading={loading}>
                {modalMode === 'create' ? 'Créer' : 'Modifier'}
              </Button>,
            ]
          )
        }
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={modalMode === 'view'}
        >
          {formFields.map((field) => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={field.rules}
            >
              {field.component}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </Container>
  );
};

export default ReferenceManagement;